import { useParams } from 'react-router-dom'
import { adminNavItems } from '../../app/nav'
import { EmptyState } from '../../components/common/Status'
import { NavItem } from '../../components/ui/nav-item'
import { useAuthStore } from '../../stores/auth'
import { hasAnyPermission, parseTokenClaims } from '../../lib/authz'
import { UsersSection } from './sections/UsersSection'
import { RolesSection } from './sections/RolesSection'
import { AuditSection } from './sections/AuditSection'
import { VolunteerSection } from './sections/VolunteerSection'
import { TasksSection } from './sections/TasksSection'
import { PairCodesSection } from './sections/PairCodesSection'

const sections = ['users', 'roles', 'audit', 'volunteers', 'tasks', 'pair-codes'] as const
type SectionKey = (typeof sections)[number]

/** 各区块对应权限码（与 backend/internal/auth/principal.go 一致） */
const SECTION_PERMISSIONS: string[] = [
  'users.read',
  'roles.read',
  'permissions.read',
  'audit.read',
  'volunteer.applications.read',
  'tasks.read',
] as const

const sectionMeta: Record<SectionKey, { title: string; description: string }> = {
  users: { title: '用户与权限', description: '平台账户、区域范围与状态（v2 RBAC）' },
  roles: { title: '角色与权限码', description: '角色定义与权限码目录' },
  audit: { title: '审计日志', description: '高危操作与权限变更的不可抵赖记录' },
  volunteers: { title: '志愿者审核', description: '申请审核、证书发放记录与志愿者协议' },
  tasks: { title: '任务调度', description: '应急任务派发与状态流转' },
  'pair-codes': { title: '观测站配对码', description: '固定观测站配对码发放、绑定与回收' },
}

/** 管理后台布局：子导航 + v2 令牌权限 gate */
export function AdminLayout() {
  const { section } = useParams<{ section?: string }>()
  const token = useAuthStore((s) => s.token)
  const active = (sections.includes((section ?? 'users') as SectionKey) ? section : 'users') as SectionKey

  if (!token) {
    return (
      <div className="panel-glass rounded-xl">
        <EmptyState
          title="需要登录令牌"
          description="管理端基于 /api/v2 Bearer JWT。请先在登录页粘贴开发令牌。"
        />
      </div>
    )
  }

  const claims = parseTokenClaims(token)
  if (!hasAnyPermission(claims, SECTION_PERMISSIONS)) {
    return (
      <div className="panel-glass rounded-xl">
        <EmptyState
          title="权限不足"
          description="当前账号未持有管理端权限码（如 users.read / audit.read / tasks.read）。请联系超级管理员授权。"
        />
      </div>
    )
  }

  const meta = sectionMeta[active]

  return (
    <div className="grid gap-5 lg:grid-cols-[220px_1fr]">
      <nav className="panel-glass h-fit rounded-xl p-2">
        <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-[.18em] text-muted-foreground">
          超级管理员工作区
        </div>
        {adminNavItems.map((item, idx) => (
          <NavItem key={item.to} to={`/admin/${sections[idx]}`} icon={item.icon} label={item.label} className="mb-0.5 text-xs" />
        ))}
      </nav>

      <div>
        <div className="mb-4">
          <h1 className="text-lg font-semibold text-foreground">{meta.title}</h1>
          <p className="mt-0.5 text-xs text-muted-foreground">{meta.description}</p>
        </div>
        {active === 'users' && <UsersSection />}
        {active === 'roles' && <RolesSection />}
        {active === 'audit' && <AuditSection />}
        {active === 'volunteers' && <VolunteerSection />}
        {active === 'tasks' && <TasksSection />}
        {active === 'pair-codes' && <PairCodesSection />}
      </div>
    </div>
  )
}
